import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpStatusCode,
} from "@angular/common/http";
import { inject, Injectable } from "@angular/core";

import { EMPTY, from, Observable, of, throwError } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";

import { Navigate } from "@ngxs/router-plugin";
import { Store } from "@ngxs/store";

import { Nullable } from "@angular-ru/cdk/typings";

import { PageName } from "@core/enums/defaults.enum";
import { IProceedRule, PROCEED_WITH_ERROR } from "@core/helpers/proceed-error.helper";
import { getUserStartPage } from "@core/helpers/user-start-page.helper";
import { ICurrentUser } from "@core/interfaces/current-user.interface";
import { ToastService } from "@core/services/toast.service";
import { CoreStateSelector } from "@core/store/core.selectors";

import { AuthLoginPageStatus } from "../../../auth-page/enums/auth.enum";
import { AuthPageActions } from "../../../auth-page/store/auth.actions";
import { AuthPageState } from "../../../auth-page/store/auth.state";

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    readonly #store: Store = inject(Store);
    readonly #toastService: ToastService = inject(ToastService);

    public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const token: Nullable<string> = this.#store.selectSnapshot(AuthPageState.getToken);

        if (token) {
            req = req.clone({ setHeaders: { token } });
        }

        return next
            .handle(req)
            .pipe(catchError((error: HttpErrorResponse) => this.#handleError(req, error)));
    }

    #handleError(
        req: HttpRequest<unknown>,
        error: HttpErrorResponse
    ): Observable<HttpEvent<unknown>> {
        const TOTAL_SUM_URL: string = "/api/admin-invoices/total-sum/";
        const proceeds: IProceedRule | false =
            req.context.has(PROCEED_WITH_ERROR) && req.context.get(PROCEED_WITH_ERROR);

        if (proceeds && proceeds[error.status] !== undefined) {
            const handler = proceeds[error.status];

            if (handler === null) {
                return of(error) as unknown as Observable<HttpEvent<unknown>>;
            }

            return handler(error);
        }

        if (
            req.url.includes(TOTAL_SUM_URL) &&
            (error.status === HttpStatusCode.GatewayTimeout || error.status === 524)
        ) {
            return throwError(() => error);
        }

        if (error.status >= HttpStatusCode.InternalServerError) {
            this.#store.dispatch(new Navigate([PageName.Maintenance]));
            return throwError(() => error);
        }

        if (error.status === HttpStatusCode.UnprocessableEntity) {
            return throwError(() => error);
        }

        if (!error.error) {
            this.#store.dispatch(new AuthPageActions.Logout());
            return EMPTY;
        }

        if (error.error instanceof Blob) {
            return from(error.error.text()).pipe(
                switchMap((text: string) => {
                    try {
                        const parsed = JSON.parse(text);
                        if (parsed.errors) {
                            // TODO переписать когда примут решение по ошибкам на бэке, а то так себе костыль
                            const cyrillicMessage = parsed.errors.find((e: string) =>
                                /[а-яёА-ЯЁ]/.test(e)
                            );
                            if (cyrillicMessage) {
                                this.#toastService.showErrorMessage(cyrillicMessage);
                            }
                        }
                    } catch {}
                    return throwError(() => error);
                })
            );
        }

        if (error.error.errors) {
            this.#toastService.showErrorMessage(error.error.errors);

            return throwError(() => error);
        }

        if (error.status === HttpStatusCode.Unauthorized) {
            this.#store.dispatch(
                new AuthPageActions.Logout({ [AuthLoginPageStatus.SessionFailed]: true })
            );
            return EMPTY;
        }

        if (error.status === HttpStatusCode.Forbidden) {
            const user: Nullable<ICurrentUser> = this.#store.selectSnapshot(
                CoreStateSelector.getUser
            );
            const startPage: PageName = getUserStartPage(user);

            this.#store.dispatch(
                new Navigate([startPage], {
                    [AuthLoginPageStatus.AccessDenied]: true,
                })
            );
            return EMPTY;
        }

        if (error.status !== HttpStatusCode.Unauthorized) {
            this.#toastService.showErrorMessage(error.message);
        }

        return throwError(() => error);
    }
}
